import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { sellsService } from '../../../services';
import { Button } from '@material-ui/core';
import './style.css';

type Product = {
  id: string;
  name: string;
  sku: string;
  shippingUnitCost: number;
  retailPrice?: number;
  fullPrice?: number;
  fullPrice2?: number;
  fullPrice3?: number;
  gridPrice?: number;
};

type ProductCar = {
  idProduct: string;
  quantity: number;
  price: number;
  cost: number;
  sellerComission: number;
  collaboratorComission: number;
  sku: string;
};

type Venta = {
  nroVenta: string;
  deliverydate: string;
  deliverycost: number;
  totalprice: number;
  totalcost: number;
  zone: number;
  seller: { firstName: string; lastName: string };
  client: {
    firstname: string;
    lastname: string;
    address: string;
    phone: string;
    locality: string;
    dni: string;
    beetwenstreets: string;
    comments: string;
    ventaFlete: number;
    typeclient: number;
  };
  product: Product[];
  productCar: ProductCar[];
};

const getPriceByTypeclient = (product: Product | undefined, typeclient?: number) => {
  const tc = Number(typeclient);
  switch (tc) {
    case 1:
      return product?.retailPrice;
    case 4:
      return product?.fullPrice2;
    case 5:
      return product?.fullPrice3;
    case 6:
      return product?.gridPrice;
    case 2:
    case 3:
    default:
      return product?.fullPrice;
  }
};

const getUnitPrice = (
  productoCar: ProductCar,
  productInfo: Product | undefined,
  typeclient?: number
) => {
  const productCarPrice = productoCar?.price;
  const typeclientPrice = getPriceByTypeclient(productInfo, typeclient);
  return Number(typeclient) === 4 ? typeclientPrice : productCarPrice ?? typeclientPrice;
};

const calcularTotalCost = (
  productosCar: ProductCar[],
  productos: Product[],
  zone: number,
  typeclient?: number
) => {
  if (!productosCar?.length || !productos?.length) return 0;

  return productosCar.reduce((total, productoCar) => {
    const productInfo = productos.find((p) => p.id === productoCar.idProduct);

    const quantity = Number(productoCar.quantity) || 0;
    const unitPrice = getUnitPrice(productoCar, productInfo, typeclient);
    const price = Number(unitPrice) || 0;

    const baseShippingUnitCost = productInfo ? Number(productInfo.shippingUnitCost) || 0 : 0;
    const shippingUnitCost = (typeclient === 2 || typeclient === 4) ? baseShippingUnitCost / 2 : baseShippingUnitCost;

    // Zona 5: mismo criterio que en el remito individual
    if (zone === 5) return total + quantity * price;

    return total + quantity * (price + shippingUnitCost);
  }, 0);
};

const decode_utf8 = (str: string) => {
  try {
    let decoded = new TextDecoder("utf-8").decode(new TextEncoder().encode(str));
    return decoded
      .replace(/Ã³/g, "ó")
      .replace(/Ã¡/g, "á")
      .replace(/Ã©/g, "é")
      .replace(/Ã­/g, "í")
      .replace(/Ãº/g, "ú")
      .replace(/Ã±/g, "ñ");
  } catch {
    return str;
  }
};

const formatFecha = (fechaISO: string) => {
  if (!fechaISO) return '';
  const fecha = new Date(fechaISO);
  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(fecha);
};

const RemitosMasivos = () => {
  const [ventas, setVentas] = useState<Venta[]>([]);
  const location = useLocation();

  const nroVentas = new URLSearchParams(location.search).getAll('nroVenta');

  useEffect(() => {
    const fetchVentas = async () => {
      const resultados: Venta[] = [];
      for (const nro of nroVentas) {
        const data = await sellsService.find(nro);

        // Igual que en el remito individual: recalculamos totalcost para que incluya flete unitario
        if (data && Array.isArray(data.productCar) && Array.isArray(data.product)) {
          const totalCostCalculado = calcularTotalCost(
            data.productCar,
            data.product,
            data.zone,
            data.client?.typeclient
          );

          resultados.push({ ...data, totalcost: totalCostCalculado });
        } else {
          resultados.push(data);
        }
      }
      setVentas(resultados);
    };
    fetchVentas();
  }, [nroVentas]);

  useEffect(() => {
    if (ventas.length > 0) {
      setTimeout(() => {
        window.print();
        window.onafterprint = () => window.close();
      }, 1000);
    }
  }, [ventas]);

  return (
    <div className="remito-container">
      {ventas.map((venta) => (
        <div key={venta.nroVenta} style={{ pageBreakAfter: 'always' }}>
          {[1, 2].map((_, idx) => (
            <div key={idx} className="remito-duplicado">
              <div className="remito-header">
                <div className="remito-vendedor">
                  <img
                    src={`https://quickchart.io/qr?text=${venta.nroVenta}&size=150`}
                    alt="QR"
                  />
                </div>
                <div className="remito-info">
                  <h2>{decode_utf8(venta.client.locality)}</h2>
                  <p><strong>Fecha:</strong> {formatFecha(venta.deliverydate)}</p>
                  <p><strong>N° Pedido:</strong> {venta.nroVenta}</p>
                  <p><strong>Vendedor:</strong> {`${venta.seller.firstName} ${venta.seller.lastName}`}</p>
                  <p><strong>{idx === 0 ? 'ORIGINAL' : 'DUPLICADO'}</strong></p>
                </div>
              </div>

              <div className="remito-cliente">
                <p>
                  <strong>Nombre:</strong> {decode_utf8(`${venta.client.firstname} ${venta.client.lastname}`)}{' '}
                  <span><strong>DNI:</strong> {venta.client.dni}</span>
                </p>
                <p><strong>Teléfono:</strong> {venta.client.phone}</p>
                <p><strong>Dirección:</strong> {decode_utf8(venta.client.address)}</p>
                <p><strong>Entre Calles:</strong> {decode_utf8(venta.client.beetwenstreets)}</p>
              </div>

              <table className="remito-productos">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Artículo</th>
                    <th>Cant</th>
                    <th>Precio total</th>
                  </tr>
                </thead>
                <tbody>
                  {venta.productCar.map((prod) => {
                    const info = venta.product.find(p => p.id === prod.idProduct);
                    const unitPrice = getUnitPrice(prod, info, venta.client?.typeclient);
                    const quantity = Number(prod.quantity) || 0;
                    return (
                      <tr key={prod.idProduct}>
                        <td>{info?.sku}</td>
                        <td>{info?.name}</td>
                        <td>{prod.quantity}</td>
                        <td>{quantity * (Number(unitPrice) || 0)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="remito-comentarios">
                <p><strong>{decode_utf8(venta.client.comments)}</strong></p>
              </div>

              <div className="remito-right">
                <div className="remito-total">
                  <p><strong>Flete:</strong> $ {venta.totalcost - venta.totalprice}</p>
                  <p><strong>Adic. Loc.:</strong> $ {venta ? venta.deliverycost : 0}</p> 
                  <p><strong>Total:</strong> $ {venta.totalcost + venta.deliverycost + venta.client.ventaFlete}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default RemitosMasivos;
